import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, Output, EventEmitter, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbCollapseModule, NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
//---Services
import { NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { User } from '../../../core/models/user/user';
import { AuthService } from '../../../core/services/auth/auth.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { UserService } from '../../../core/services/user/user.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { TopBannerButton } from '../banner/top-banner/top-banner.component';
import { InfoTooltipComponent } from '../tooltips/info-tooltip/info-tooltip.component';
import { SubscriptionInfoComponent } from '../../../features/front/parameters/account-info/subscription-info/subscription-info.component';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-header',
    imports: [NgbCollapseModule, NgbDropdownModule, NgIf, TranslateModule],
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent  implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private userService = inject(UserService);
  private alertService = inject(AlertService);
  private cdr = inject(ChangeDetectorRef);


  @Output() openSidebarEvent = new EventEmitter<void>();
  protected isMenuCollapsed = true;
  protected showPanel = false;
  protected user: User | undefined;
  creationDate!: Date;
  protected appVersion = environment.version;
  public bannerButtons: TopBannerButton[] = [];
  @ViewChild('profileDropdown') private profileDropdownRef?: NgbDropdown;
  private shouldLogoutAfterPanel = false;
  private userUpdateSubscription?: Subscription;

  ngOnInit() {
    this.loadUser();
    this.bannerButtons = [
      {
        label: 'Donner son avis',
        type: 'thirdcolor',
        icon: 'fa fa-file-alt',
        action: () => {
          window.open(
            'https://docs.google.com/forms/d/e/1FAIpQLSdD6Lb8g721_xpNicJxP45BSOtpT21QyJ04Wg2ygdgxLlENqw/viewform?pli=1',
            '_blank',
            'noopener'
          );
          this.logout();
        }
      },
      {
        label: $localize`:@@close:Se déconnecter`,
        type: 'primary',
        icon:'fa fa-times',
        action: () => this.closePanelAndLogout()
      }
    ];

    // Listen for storage changes to update user info
    window.addEventListener('storage', this.handleStorageChange);
    
    // Also listen for custom user update events
    window.addEventListener('userUpdated', this.handleUserUpdate);
  }

  ngOnDestroy() {
    if (this.userUpdateSubscription) {
      this.userUpdateSubscription.unsubscribe();
    }
    // Remove event listeners
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('userUpdated', this.handleUserUpdate);
  }

  private handleStorageChange = (e: StorageEvent) => {
    if (e.key === 'user') {
      this.loadUser();
    }
  }

  private handleUserUpdate = () => {
    this.loadUser();
    this.cdr.detectChanges();
  }

  private loadUser() {
    // Always refresh user from server to ensure we have latest data (especially after reconnection)
    this.userService.getCurrentUser().subscribe({
      next: (user) => {
        if (user) {
          this.storageService.setUser(user);
          this.user = user;
          if (this.user?.creationDate) {
            this.creationDate = new Date(this.user.creationDate);
          }
          this.cdr.detectChanges();
        } else {
          // Fallback to localStorage if server call fails
          this.user = this.storageService.getUser();
          if (this.user?.creationDate) {
            this.creationDate = new Date(this.user.creationDate);
          }
        }
      },
      error: () => {
        // Fallback to localStorage if server call fails
    this.user = this.storageService.getUser();
    if (this.user?.creationDate) {
      this.creationDate = new Date(this.user.creationDate);
    }
      }
    });
  }

  public goToOldInterface() {
    let redirectUrl = 'https://old-bf.budgetfacile.com';
    if (window.location.hostname == 'beta-app.budgetfacile.com') {
      redirectUrl = 'https://beta.budgetfacile.com';
    }
    if (window.location.hostname == 'mescomptes.budgetfacile.com') {
      redirectUrl = 'https://old-bf.budgetfacile.com';
    }
    window.location.href = redirectUrl;
  }

  public logout() {
    localStorage.removeItem('accountsSynchronizedOnce');
    localStorage.removeItem('user');
    localStorage.removeItem('dashboardGuideShown');
    localStorage.removeItem('dashboardwelcomeShown'); 
    localStorage.removeItem('dark-mode');
    localStorage.removeItem('hasConnectedBefore');
    InfoTooltipComponent.resetAllTooltips();
    this.authService.logout();
    this.router.navigate(['/authentication/login'], { queryParams: { disconnected: '1' } });
  }

  public onLogoutClick() {
    this.shouldLogoutAfterPanel = true;
    this.showPanel = true;
  }


  closePanel() {
    this.showPanel = false;
    // Réinitialiser le flag si on ferme avec le X
    this.shouldLogoutAfterPanel = false;
    if (this.profileDropdownRef) {
      setTimeout(() => this.profileDropdownRef?.open(), 0);
    }
  }
  
  closePanelAndLogout() {
    this.showPanel = false;
    if (this.shouldLogoutAfterPanel) {
      this.shouldLogoutAfterPanel = false;
      this.logout();
    }
  }

  // User type getters (avoid method calls in template - ESLint)
  get isPremiumUser(): boolean {
    return this.userService.isPremiumUser(this.user);
  }

  get isPremiumGiftedUser(): boolean {
    return this.userService.isPremiumGiftedUser(this.user);
  }

  get isPremiumDiscoveryUser(): boolean {
    return this.userService.isPremiumDiscoveryUser(this.user);
  }

  get isStandardUser(): boolean {
    return this.userService.isStandardUser(this.user);
  }

  get isStandardAutoImportUser(): boolean {
    return this.userService.isStandardAutoImportUser(this.user);
  }

  get isEssentielUser(): boolean {
    return this.userService.isEssentielUser(this.user);
  }

  get isEssentielGiftedUser(): boolean {
    return this.userService.isEssentielGiftedUser(this.user);
  }

  get isProUser(): boolean {
    return this.userService.isProUser(this.user);
  }

  get isProGiftedUser(): boolean {
    return this.userService.isProGiftedUser(this.user);
  }

  openSubscriptionInfo(): void {
    this.alertService.openWithContent(SubscriptionInfoComponent).result.catch(() => {});
  }

}
